import { Checkbox, Input, Table } from 'antd';
import { StudentResultSheetModelType } from '@models/StudentResultSheet';
import { StudentResultModelType } from '@models/StudentResult';
import { observer } from 'mobx-react';
import { useMst } from '@models/Root';
import { toJS } from 'mobx';
import { getStudentResult } from '@modules/resultSheet';
import React from 'react';
import { ColumnsType } from 'antd/lib/table/interface';

export const StudentResults = observer((mainRecord: StudentResultSheetModelType) => {
  const { studentResultSheets, isOwnResultSheet, status } = useMst().resultSheet;

  const studentResultSheet = studentResultSheets.find((data) => mainRecord.id === data.id);

  const resultSheetEditable = isOwnResultSheet && status !== 'approved';

  const columns: ColumnsType<StudentResultModelType> = [
    {
      title: 'Name',
      dataIndex: 'batchSubjectExtraSlotName',
      render: (_, record) => {
        return record.batchSubjectExtraSlotName || record.assessmentTitle;
      },
    },
    { title: 'Allocated Marks', width: 150, dataIndex: 'allocatedMarks', key: 'allocatedMarks' },
    {
      title: 'First Attempt',
      children: [
        {
          title: 'Marks',
          dataIndex: 'firstAttemptMarks',
          width: 200,
          render: (_, record: StudentResultModelType) => {
            return (
              <Input
                disabled={!resultSheetEditable}
                value={record.firstAttemptMarks}
                min={0}
                onChange={(event) => {
                  const studentResult = getStudentResult({
                    studentResultSheet,
                    batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
                    assessmentId: record.assessmentId,
                  });

                  if (studentResult) {
                    studentResult.updateFirstAttemptMarks(parseInt(event.target.value, 10));
                  }
                }}
                type="number"
              />
            );
          },
        },
        {
          title: 'NYC',
          dataIndex: 'isFirstAttemptNYC',
          key: 'isFirstAttemptNYC',
          width: 30,
          render: (_, record: StudentResultModelType) => {
            const studentResult = getStudentResult({
              studentResultSheet,
              batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
              assessmentId: record.assessmentId,
            });

            return (
              <Checkbox
                disabled={!resultSheetEditable}
                checked={studentResult.isFirstAttemptNYC}
                onChange={(event) => {
                  studentResult.updateFirstAttemptNYC(event.target.checked);

                  if (!event.target.checked) {
                    studentResult.updateSecondAttemptMarks(undefined);
                  }
                }}
              />
            );
          },
        },
      ],
    },
    {
      title: 'Second Attempt',
      children: [
        {
          title: 'Marks',
          dataIndex: 'secondAttemptMarks',
          key: 'secondAttemptMarks',
          width: 200,
          render: (_, record: StudentResultModelType) => {
            const studentResult = getStudentResult({
              studentResultSheet,
              batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
              assessmentId: record.assessmentId,
            });

            return (
              <Input
                min={0}
                disabled={!resultSheetEditable || !record.isFirstAttemptNYC}
                value={record.secondAttemptMarks}
                onChange={(event) => {
                  if (event.target.value === '') {
                    studentResult.updateSecondAttemptMarks(0);
                  } else {
                    const marks = parseInt(event.target.value, 10);
                    if (marks >= 0) {
                      studentResult.updateSecondAttemptMarks(marks);
                    }
                  }
                }}
                type="number"
              />
            );
          },
        },
        {
          title: 'NYC',
          dataIndex: 'isSecondAttemptNYC',
          key: 'isSecondAttemptNYC',
          width: 30,
          render: (_, record: StudentResultModelType) => {
            const studentResult = getStudentResult({
              studentResultSheet,
              batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
              assessmentId: record.assessmentId,
            });

            return (
              <Checkbox
                checked={studentResult.isSecondAttemptNYC}
                disabled={!resultSheetEditable || !record.isFirstAttemptNYC}
                onChange={(event) => {
                  studentResult.updateSecondAttemptNYC(event.target.checked);

                  if (!event.target.checked) {
                    studentResult.updateThirdAttemptMarks(undefined);
                  }
                }}
              />
            );
          },
        },
      ],
    },
    {
      title: 'Third Attempt',
      children: [
        {
          title: 'Marks',
          dataIndex: 'thirdAttemptMarks',
          key: 'thirdAttemptMarks',
          width: 200,
          render: (_, record: StudentResultModelType) => {
            const studentResult = getStudentResult({
              studentResultSheet,
              batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
              assessmentId: record.assessmentId,
            });

            return (
              <Input
                disabled={!resultSheetEditable || !record.isSecondAttemptNYC}
                value={record.thirdAttemptMarks}
                onChange={(event) => {
                  studentResult.updateThirdAttemptMarks(parseInt(event.target.value, 10));
                }}
                type="number"
                min={0}
              />
            );
          },
        },
        {
          title: 'NYC',
          dataIndex: 'isThirdAttemptNYC',
          key: 'isThirdAttemptNYC',
          width: 30,
          render: (_, record: StudentResultModelType) => {
            const studentResult = getStudentResult({
              studentResultSheet,
              batchSubjectExtraSlotId: record.batchSubjectExtraSlotId,
              assessmentId: record.assessmentId,
            });

            return (
              <Checkbox
                checked={studentResult.isThirdAttemptNYC}
                disabled={!resultSheetEditable || !record.isSecondAttemptNYC}
                onChange={(event) => {
                  studentResult.updateThirdAttemptNYC(event.target.checked);
                }}
              />
            );
          },
        },
      ],
    },
  ];

  return (
    <Table
      bordered
      columns={columns}
      dataSource={[...toJS(studentResultSheet?.studentResults)]}
      pagination={false}
      rowKey={(record) => record.assessmentId || record.batchSubjectExtraSlotId}
    />
  );
});
